import type { FirebaseRepository } from '../models/FirebaseRepository'
import { Cubit } from './core'
import {
  createGlobalAuthor,
  deleteGlobalAuthorImage,
  getGlobalAuthorList,
  hideGlobalAuthor,
  searchGlobalAuthors,
  unhideGlobalAuthor,
  updateGlobalAuthor,
  uploadGlobalAuthorImage,
  type GlobalAuthorFieldsForUpload,
} from '../firestore/GlobalAuthor'
import { action, computed, makeObservable, observable } from 'mobx'
import { GlobalAuthor } from '../models/GlobalAuthor'
import type { StaticModelCollection } from '../firestore-mobx/model'

export class GlobalAuthorCubit extends Cubit {
  repository: FirebaseRepository
  authors: StaticModelCollection<GlobalAuthor>
  @observable searchResults: GlobalAuthor[] = []

  constructor(repository: FirebaseRepository) {
    super()
    this.repository = repository
    this.authors = GlobalAuthor.emptyCollection(repository)
    makeObservable(this)
  }

  async initialize(): Promise<void> {
    this.addStream(getGlobalAuthorList(this.repository), (authors) => {
      this.authors.replaceModels(authors)
    })
  }

  @computed
  get sortedAuthors() {
    return [...this.authors.models].sort((a, b) =>
      a.data.authorName.localeCompare(b.data.authorName)
    )
  }

  @computed
  get visibleAuthors() {
    return this.sortedAuthors.filter((a) => !a.data.hidden)
  }

  @action
  searchAuthors = async (term: string) => {
    const results = await searchGlobalAuthors(this.repository, { term })
    this.searchResults = results
    return results
  }

  @action
  createAuthor = async (authorData: GlobalAuthorFieldsForUpload) => {
    return createGlobalAuthor(this.repository, authorData)
  }

  @action
  updateAuthor = async (
    authorId: string,
    authorData: GlobalAuthorFieldsForUpload
  ) => {
    return updateGlobalAuthor(this.repository, authorId, authorData)
  }

  @action
  uploadAuthorImage = async (authorId: string, file: File) => {
    return uploadGlobalAuthorImage(this.repository, { authorId, file })
  }

  @action
  deleteAuthorImage = async (authorId: string) => {
    return deleteGlobalAuthorImage(this.repository, { authorId })
  }

  @action
  hideAuthor = async (authorId: string) => {
    return hideGlobalAuthor(this.repository, authorId)
  }

  @action
  unhideAuthor = async (authorId: string) => {
    return unhideGlobalAuthor(this.repository, authorId)
  }
}
